import { ListFilter } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NotificationBadgeVariant } from '~/components/shared/componentStyles/NotificationBadge.style'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { FilterModal } from '~/components/shared/FilterModal'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { NotificationBadge } from '~/components/shared/NotificationBadge'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { FilterSection } from '~/types/Shared'
import { Size } from '~/types/ThemeTypes'

interface FilterButtonProps {
  filterData?: FilterSection[]
  title?: string
  activeFiltersCount?: number
  onFilterChangesApplied?: (updatedFilters: FilterSection[]) => void
  onFilterModalClosed?: () => void
  useQuickActionButton?: boolean
  isLoading?: boolean
}

export const FilterButton = ({
  filterData = [],
  title,
  activeFiltersCount = 0,
  onFilterChangesApplied,
  onFilterModalClosed,
  useQuickActionButton,
  isLoading = false,
}: FilterButtonProps) => {
  const { t } = useTranslation()
  const [modalOpen, setModalOpen] = useState(false)
  const resolvedTitle = title ?? t('filterModal.buttonTitle', 'Filters')

  const handleButtonClick = () => {
    setModalOpen(true)
  }

  const handleModalClose = () => {
    setModalOpen(false)
    onFilterModalClosed?.()
  }

  const handleFiltersApplied = (updatedFilterData: FilterSection[]) => {
    onFilterChangesApplied?.(updatedFilterData)
    handleModalClose()
  }

  const badgeVariant = NotificationBadgeVariant.Notification

  const badgeNode = useMemo(() => {
    if (activeFiltersCount === 0) {
      return null
    }
    return (
      <NotificationBadge
        size={Size.sm}
        variant={badgeVariant}
        count={activeFiltersCount}
      />
    )
  }, [activeFiltersCount, badgeVariant])

  const tooltipText = useMemo(() => {
    if (activeFiltersCount === 0) return resolvedTitle
    if (activeFiltersCount === 1)
      return t(
        'filterModal.tooltipOneApplied',
        '{{title}} ({{count}} filter applied)',
        { title: resolvedTitle, count: activeFiltersCount },
      )
    return t(
      'filterModal.tooltipManyApplied',
      '{{title}} ({{count}} filters applied)',
      { title: resolvedTitle, count: activeFiltersCount },
    )
  }, [resolvedTitle, activeFiltersCount, t])

  return (
    <>
      {useQuickActionButton ? (
        <QuickActionButton
          actionIcon={ListFilter}
          tooltipText={tooltipText}
          tooltipDisabled={modalOpen}
          variant={
            activeFiltersCount > 0
              ? QuickActionButtonVariants.Active_200
              : QuickActionButtonVariants.Default_200
          }
          onClick={handleButtonClick}
          disabled={isLoading}
        />
      ) : (
        <MangoButton
          variant={MangoButtonVariant.Default_200}
          leftIcon={ListFilter}
          onClick={handleButtonClick}
          rightNode={badgeNode}
          disabled={isLoading}
        >
          {resolvedTitle}
        </MangoButton>
      )}
      <FilterModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        filterSections={filterData}
        onFiltersApplied={handleFiltersApplied}
      />
    </>
  )
}
